import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useRouteMatch } from 'react-router-dom'

import { BenefitDesignStep } from 'adminDashboard/benefitDetails/design/qsehra/DesignStepsMapping'

import {
  BenefitDetailsResponse,
  NotifyEmployeesFormValues,
  RenewalPlanPeriodResponse,
  getBenefitDetails,
  getRenewalPlanPeriod,
  inviteEmployees,
  notifyEmployees,
  setQsehraBenefitRedesignDecision,
} from 'core'
import benefitPlanPeriods, { PlanPeriodDto } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'
import getBenefitProductClasses, {
  BenefitProductClassResponse,
} from 'core/application/adminDashboard/benefitDetails/getBenefitProductClasses'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'

import useQueryParams from 'common/hooks/useQueryParams'
import useQueryRequest from 'common/hooks/useQueryRequest'

import { Alert, BenefitPlanPeriodDropDown, Button, Col, Row, Skeleton } from 'common/components'
import { parseZoneFormat } from 'common/dateFormatters'
import { useCommandRequest } from 'common/hooks'

import Card from '../../../../../common/components/dataDisplay/Card'
import { DESIGN_BENEFIT_PATH } from '../../../design'
import ProductClassDetails from '../../../design/qsehra/employeeClasses/productClassWizard/productClassDetails'
import SummaryEmpty from '../../SummaryEmpty'
import SummaryTitle from '../../SummaryTitle'
import RenewalDesignInfoBox from '../../common/RenewalDesignInfoBox'
import UpdateNotificationDateModal from '../../common/UpdateNotificationDateModal'

const QsehraDesignBenefitSummary: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const match = useRouteMatch()
  const queryParams = useQueryParams()

  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState<PlanPeriodDto | null>()
  const [showNotificationDateEdit, setShowNotificationDateEdit] = useState(false)
  const benefitDesignFullPath = `${match.path}/${DESIGN_BENEFIT_PATH}/`
  const [planPeriods] = useQueryRequest<PlanPeriodDto[]>(null, () => benefitPlanPeriods())
  const [renewalPlanPeriod] = useQueryRequest<RenewalPlanPeriodResponse>(null, () => getRenewalPlanPeriod())
  const [planPeriodDetails, isPlanPeriodDetailsLoading, sendGetPlanPeriodDetails] =
    useQueryRequest<BenefitDetailsResponse>(null)
  const [sendCommand, isCommandLoading] = useCommandRequest()

  const planPeriodIdQueryParam = queryParams.get('planPeriodId')

  const isSelectPlanPeriodTheRenewalPlanPeriod = selectedPlanPeriod?.id === renewalPlanPeriod?.id

  useEffect(() => {
    if (planPeriods && planPeriods.length > 0) {
      const found = planPeriodIdQueryParam
        ? planPeriods.find((x) => x.id === planPeriodIdQueryParam)
        : planPeriods.find((x) => x.active == true)

      if (found) {
        setSelectedPlanPeriod(found)
      }
    }
  }, [planPeriods])

  useEffect(() => {
    if (selectedPlanPeriod) {
      ;(async () => {
        await sendGetPlanPeriodDetails(() => getBenefitDetails(selectedPlanPeriod.id))
      })()
    }
  }, [selectedPlanPeriod])

  const handleNotificationModalSubmit = async (values: NotifyEmployeesFormValues) => {
    if (selectedPlanPeriod?.id === renewalPlanPeriod?.id) await sendCommand(() => notifyEmployees(values))
    else await sendCommand(() => inviteEmployees(values))
    await sendGetPlanPeriodDetails(() => getBenefitDetails(selectedPlanPeriod.id))
  }

  const handleBenefitStartDataEdit = () => {
    history.push(`${benefitDesignFullPath}${selectedPlanPeriod.id}/${BenefitDesignStep.BenefitStartDate}`)
  }

  const handleEligibleEmployeesEdit = () => {
    history.push(`${benefitDesignFullPath}${selectedPlanPeriod.id}/${BenefitDesignStep.EmployeeClasses}`)
  }

  const handleNotificationDateEdit = () => {
    setShowNotificationDateEdit(true)
  }

  const handleBenefitDesignEdit = () => {
    history.push(`${benefitDesignFullPath}${selectedPlanPeriod.id}/${BenefitDesignStep.FinishBenefitDesign}`)
  }

  const onPlanPeriodChanged = (planPeriodId: string) => {
    setSelectedPlanPeriod(planPeriods.find((x) => x.id === planPeriodId))
  }

  return (
    <Card
      title={
        <Row justify={'space-between'} className={'w-100'}>
          <Col>
            <SummaryTitle />
          </Col>
          <Col>
            {planPeriods && (
              <BenefitPlanPeriodDropDown
                defaultValue={planPeriodIdQueryParam}
                planPeriods={planPeriods}
                onPlanPeriodSelected={onPlanPeriodChanged}
              />
            )}
          </Col>
        </Row>
      }
      className="benefit_details__summary"
    >
      <Skeleton active loading={isPlanPeriodDetailsLoading} paragraph={{ rows: 10 }}>
        {planPeriodDetails && (
          <>
            {planPeriodDetails.benefitDesignStatus === BenefitDesignStatus.Completed ||
            planPeriodDetails.benefitDesignStatus === BenefitDesignStatus.Signed ? (
              <>
                {renewalPlanPeriod && (
                  <RenewalDesignInfoBox
                    renewalPlanPeriod={renewalPlanPeriod}
                    renewalDecisionHandler={(useSame) => setQsehraBenefitRedesignDecision({ useSame })}
                  />
                )}

                <Row className={'mb-8'}>
                  <Col span={24}>
                    <Row className="with_card">
                      <Col span={18} className="content">
                        <h5 className="mb-0">
                          <b>{t('admin-dashboard.benefit-details.summary.start-date-title')}</b>
                        </h5>
                        <h5 className="mb-0">{parseZoneFormat(planPeriodDetails.startDate)}</h5>
                      </Col>
                      <Col span={6} className="flex justify-end">
                        <Button
                          type="link"
                          className={'t-start-date'}
                          disabled={planPeriodDetails?.benefitDesignStatus === BenefitDesignStatus.Signed}
                          onClick={handleBenefitStartDataEdit}
                        >
                          {t('admin-dashboard.benefit-details.summary.edit-button')}
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                <Row className={'mb-8'}>
                  <Col span={24}>
                    <Row className="with_card">
                      <Col span={18} className="content">
                        <h5 className="mb-0">
                          <b>Eligible employees</b>
                        </h5>
                        <h5 className="mb-0">
                          {planPeriodDetails.productClasses.some((x) => x.isForFullTimeAndPartTime) ? (
                            <span>Full Time & Part Time</span>
                          ) : (
                            <span>Full Time Only</span>
                          )}
                        </h5>
                      </Col>

                      <Col span={6} className="flex justify-end">
                        <Button
                          type="link"
                          className={'t-eligible-employees'}
                          disabled={!planPeriodDetails.isEditable}
                          onClick={handleEligibleEmployeesEdit}
                        >
                          {t('admin-dashboard.benefit-details.summary.edit-button')}
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </Row>

                {planPeriodDetails.notificationDate && (
                  <>
                    {!planPeriodDetails.isEditable && planPeriodDetails.isNotificationDateEditable && (
                      <Alert
                        className={'mb-16 t-notification-has-passed'}
                        message={
                          ' The benefit notification date has already passed. If you’d like to make changes to your\n' +
                          ' benefit, please change the notification date to a future day. This will open the benefit\n' +
                          ' for edits again and your employees will receive an email with what has changed on the new\n' +
                          ' notification date'
                        }
                      ></Alert>
                    )}

                    <Row className={'mb-8'}>
                      <Col span={24}>
                        <Row className="with_card">
                          <Col span={18} className="content">
                            <h5 className="mb-0">
                              <b>Notification Date</b>
                            </h5>
                            <h5 className="mb-0">{parseZoneFormat(planPeriodDetails.notificationDate)}</h5>
                          </Col>
                          <Col span={6} className="flex justify-end">
                            <Button
                              type="link"
                              className={'t-notification-date'}
                              disabled={!planPeriodDetails.isNotificationDateEditable}
                              onClick={handleNotificationDateEdit}
                            >
                              {t('admin-dashboard.benefit-details.summary.edit-button')}
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                      {showNotificationDateEdit && (
                        <UpdateNotificationDateModal
                          title="Edit notification date"
                          visible={showNotificationDateEdit}
                          notificationDate={parseZoneFormat(planPeriodDetails.notificationDate)}
                          onCancel={() => setShowNotificationDateEdit(false)}
                          benefitStartDate={parseZoneFormat(planPeriodDetails.startDate)}
                          onSubmit={handleNotificationModalSubmit}
                          isSubmitButtonLoading={isCommandLoading}
                        />
                      )}
                    </Row>
                  </>
                )}

                <Row>
                  <Col span={24}>
                    <Card
                      title={<h5 className="mb-0">Benefit Design</h5>}
                      extra={
                        <Button
                          type="link"
                          className={'t-benefit-design'}
                          disabled={!planPeriodDetails.isEditable}
                          onClick={handleBenefitDesignEdit}
                        >
                          {t('admin-dashboard.benefit-details.summary.edit-button')}
                        </Button>
                      }
                    >
                      <Row>
                        <Col span={18}>
                          <BenefitSummary
                            planPeriodId={selectedPlanPeriod.id}
                            benefitStartDate={parseZoneFormat(planPeriodDetails.startDate)}
                          />
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                </Row>
              </>
            ) : (
              <>
                {renewalPlanPeriod &&
                isSelectPlanPeriodTheRenewalPlanPeriod &&
                renewalPlanPeriod.status == BenefitDesignStatus.NotStarted ? (
                  <RenewalDesignInfoBox
                    renewalPlanPeriod={renewalPlanPeriod}
                    renewalDecisionHandler={(useSame) => setQsehraBenefitRedesignDecision({ useSame })}
                  />
                ) : (
                  <SummaryEmpty planPeriodId={selectedPlanPeriod.id} />
                )}
              </>
            )}
          </>
        )}
      </Skeleton>
    </Card>
  )
}

export default QsehraDesignBenefitSummary

type BenefitSummaryProps = {
  planPeriodId: string
  benefitStartDate: string
}

const BenefitSummary = ({ planPeriodId, benefitStartDate }: BenefitSummaryProps) => {
  const [productClasses, areProductClassesLoading] = useQueryRequest<BenefitProductClassResponse[]>([], () =>
    getBenefitProductClasses(planPeriodId),
  )

  return (
    <Row className={'mb-8 mt-4 t-benefit-summary'}>
      <Skeleton loading={areProductClassesLoading}>
        {productClasses.map((item, index) => (
          <Col span={24} key={index}>
            <ProductClassDetails
              colSpan={24}
              key={item.id}
              collapseKey={index}
              item={item}
              benefitStartDate={benefitStartDate}
            />
          </Col>
        ))}
      </Skeleton>
    </Row>
  )
}
